import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // 10 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Contact API Service (Single Type)
export const contactService = {
  // Get contact page data (Single Type - returns single object)
  async getContactData(params = {}) {
    try {
      const response = await api.get('/contact', {
        params: {
          'populate[contactHeader][populate]': '*',
          'populate[officeLocation][populate]': '*'
        }
      });
      
      // Check if response has expected structure
      if (!response.data) {
        throw new Error('No data in response');
      }
      
      // For Single Types, the data is directly in response.data.data
      const result = {
        data: response.data.data || response.data, // Fallback if data is not nested
        meta: response.data.meta,
        success: true
      };
      
      // Validate that we have actual data
      if (!result.data) {
        throw new Error('No contact data found in API response');
      }
      
      return result;
    } catch (error) {
      console.error('Error fetching contact data:', error);
      console.error('Error response:', error.response);
      
      return {
        data: null,
        success: false,
        error: error.response?.data?.error?.message || error.message || 'Failed to fetch contact data'
      };
    }
  },

  // Submit contact form
  async submitContactForm(formData) {
    try {
      // Prepare submission data based on form type
      const submissionData = {
        form_type: formData.formType === 'partnership' ? 'partnership' : 'product/solution',
        full_name: formData.fullName,
        organization_name: formData.organizationName,
        phone_number: formData.phoneNumber,
        official_email: formData.email
      };

      // Add form type specific fields
      if (formData.formType === 'partnership') {
        submissionData.designation = formData.designation;
        submissionData.partnership_type = Array.isArray(formData.partnershipTypes) 
          ? formData.partnershipTypes.join(', ') 
          : formData.partnershipTypes;
        submissionData.company_website = formData.companyWebsite;
        
        // Add other partnership text if "other" is selected
        if (formData.partnershipTypes?.includes('other') && formData.otherPartnershipText) {
          submissionData.partnership_type += `: ${formData.otherPartnershipText}`;
        }
      } else {
        // For product/solution forms
        submissionData.solution_id = formData.productType;
        submissionData.hear_us_id = formData.hearAboutUs;
        submissionData.usage = formData.productUsage;
        submissionData.email = formData.email; // Use email field for product forms
      }

      console.log('Submitting form data:', submissionData);

      // Submit to our new CMS endpoint which handles EBS API integration
      const response = await api.post('/contact/submit', submissionData);
      
      return {
        data: response.data.data,
        success: true,
        message: response.data.message || 'Contact form submitted successfully'
      };
    } catch (error) {
      console.error('Error submitting contact form:', error);
      
      const errorMessage = error.response?.data?.error?.message || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to submit contact form';
      
      return {
        data: null,
        success: false,
        error: errorMessage
      };
    }
  }
};

// Helper function to format image URLs
export const formatImageUrl = (imageData) => {
  if (!imageData) return null;
  
  const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '');
  
  if (typeof imageData === 'object' && imageData.url) {
    const formattedUrl = imageData.url.startsWith('http') ? imageData.url : `${baseUrl}${imageData.url}`;
    return formattedUrl;
  }
  
  return null;
};

// Helper function to format contact data
export const formatContactData = (contactData) => {
  if (!contactData) return null;

  return {
    ...contactData,
    // Format contact header with proper image URLs
    contactHeader: contactData.contactHeader ? {
      ...contactData.contactHeader,
      headerImage: formatImageUrl(contactData.contactHeader.headerImage),
      backgroundImage: formatImageUrl(contactData.contactHeader.headerImage), // Use headerImage for background
    } : null,
    // Format office locations with proper image URLs
    officeLocation: contactData.officeLocation?.map(office => {
      // Handle different image data structures
      let imageArray = [];
      let singleImage = null;
      
      if (office.image) {
        if (Array.isArray(office.image)) {
          // If it's already an array
          imageArray = office.image;
          singleImage = office.image.length > 0 ? office.image[0] : null;
        } else if (typeof office.image === 'object') {
          // If it's a single image object
          imageArray = [office.image];
          singleImage = office.image;
        }
      }
      
      return {
        ...office,
        // Keep single image for backward compatibility (first image from array)
        image: singleImage ? formatImageUrl(singleImage) : null,
        // Handle multiple images - format each image in the array
        images: imageArray.map(img => formatImageUrl(img)).filter(url => url !== null),
        icon: formatImageUrl(office.icon),
      };
    }) || [],
  };
};

export default contactService;
